import {ChangeDetectionStrategy, Component, DOCUMENT, Inject} from '@angular/core';
import {MatIconRegistry} from "@angular/material/icon";
import {DomSanitizer} from "@angular/platform-browser";
import {LiveAnnouncer} from "@angular/cdk/a11y";
import {NavigationEnd, Router} from "@angular/router";
import {filter} from "rxjs/operators";
import {ThemeService} from "./_services/theme.service";
import {AuthService} from "./_services/auth.service";
import {environment} from "../environments/environment";
import {User} from "./_types/user";

@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})

export class AppComponent {
    title: string = 'vmb';
    user!: User;

    constructor(private matIconRegistry: MatIconRegistry,
                private domSanitizer: DomSanitizer,
                private themeService: ThemeService,
                private authService: AuthService,
                private router: Router,
                private liveAnnouncer: LiveAnnouncer,
                @Inject(DOCUMENT) private document: Document
    ) {
        this.setViewportHeightVariable();
        window.addEventListener('resize', (): void => this.setViewportHeightVariable());
        this.manageFocusOnNavigation();

        this.matIconRegistry.addSvgIcon(
            `boxIcon`,
            this.domSanitizer.bypassSecurityTrustResourceUrl("../assets/images/box-solid.svg")
        );
        this.matIconRegistry.addSvgIcon(
            `activeDirectory`,
            this.domSanitizer.bypassSecurityTrustResourceUrl("../assets/images/microsoft.svg")
        );

        this.themeService.savePrimaryColor(environment.primaryColor)
        this.themeService.saveAccentColor(environment.accentColor)
        this.authService.getUser().subscribe({
            next: r => {
                this.user = r;
            }
        })
        this.authService.getDynamicSettings().then((r): void => {
            if (r && r.font_accessibility && this.user) {
                if (this.user?.font_size > 1) {
                    document.documentElement.style.setProperty('--font-size', ((this.user.font_size + 99) + '%'));
                }
                if (this.user?.font_family == 'FSMeBold') {
                    document.documentElement.style.setProperty('--font-family', 'AtkinsonHyperlegible');
                } else if (this.user?.font_family != 'roboto') {
                    document.documentElement.style.setProperty('--font-family', this.user.font_family);
                }
            }
        });
    }

    setViewportHeightVariable(): void {
        const vh: number = window.innerHeight * 0.01;
        this.document.documentElement.style.setProperty('--vh', `${vh}px`);
    }

    // On each route change move keyboard focus to the routed content and announce
    // the new page title, so screen-reader and keyboard users are told where they
    // landed (the SPA title change alone is not reliably announced).
    private manageFocusOnNavigation(): void {
        this.router.events
            .pipe(filter((event): event is NavigationEnd => event instanceof NavigationEnd))
            .subscribe((): void => {
                // Defer so the routed view (and updated title) is in the DOM first.
                setTimeout((): void => {
                    const main: HTMLElement | null = this.document.getElementById('mainContent');
                    if (main) {
                        main.focus({preventScroll: true});
                    }
                    if (this.document.title) {
                        this.liveAnnouncer.announce(this.document.title).then();
                    }
                });
            });
    }
}
